import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PublicNavbar from '../Layout/PublicNavbar';
import Navbar from '../Layout/Navbar';
import Footer from '../Layout/Footer';
import { useTheme } from '../../contexts/ThemeContext';
import SEO, { BASE_URL } from '../Common/SEO';
import './Pricing.css';

function Pricing() {
  const { isAuthenticated, loading } = useAuth();
  const { isDarkMode } = useTheme();

  const freeFeatures = [
    '1 trading account',
    '10 open options positions',
    '10 stock positions',
    'Unlimited closed trades',
    'Dashboard analytics',
    'Advanced statistics',
    'Performance metrics',
    'Dark mode support',
    'Mobile-friendly interface',
  ];

  const proFeatures = [
    'Unlimited trading accounts',
    'Unlimited open positions',
    'Unlimited closed trades',
    'Unlimited stock positions',
    'Dashboard analytics',
    'Advanced statistics',
    'Performance metrics',
    'CSV export',
    'Dark mode support',
    'Mobile-friendly interface',
    'Priority support',
  ];

  if (loading) {
    return (
      <>
        {isAuthenticated ? <Navbar /> : <PublicNavbar />}
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title="Pricing Plans - Free Forever & Pro $4.99/mo"
        description="Options Tracker pricing - Free forever with limits, or $4.99/month for unlimited Pro access. No credit card required to start."
        canonicalPath="/pricing"
        keywords="options tracker pricing, options trading software cost, free options tracker, wheel strategy tracker price"
      />
      {/* SoftwareApplication structured data for rich search results */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Options Tracker",
          "description": "Options trading tracker with free and pro plans. Track CSPs, covered calls, LEAPS, and stock positions.",
          "image": `${BASE_URL}/og-image.png`,
          "applicationCategory": "FinanceApplication",
          "operatingSystem": "Web",
          "offers": [
            {
              "@type": "Offer",
              "name": "Free Plan",
              "url": `${BASE_URL}/pricing`,
              "priceCurrency": "USD",
              "price": "0",
              "priceValidUntil": "2027-12-31",
              "availability": "https://schema.org/InStock"
            },
            {
              "@type": "Offer",
              "name": "Pro Plan",
              "url": `${BASE_URL}/pricing`,
              "priceCurrency": "USD",
              "price": "4.99",
              "priceValidUntil": "2027-12-31",
              "availability": "https://schema.org/InStock"
            }
          ]
        })
      }} />
      {isAuthenticated ? <Navbar /> : <PublicNavbar />}
      <div className={`pricing-container ${isDarkMode ? 'dark' : ''}`}>
        {/* Hero Section */}
        <section className="pricing-hero">
          <div className="pricing-hero-content">
            <h1>Free Forever, Upgrade When Ready</h1>
            <p className="pricing-subtitle">
              Start tracking your options trades for free. Upgrade to Pro for unlimited access.
            </p>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="pricing-cards">
          <div className="pricing-card">
            <div className="pricing-header">
              <h2>Free</h2>
              <div className="pricing-price">
                <span className="price-amount">$0</span>
                <span className="price-period">/month</span>
              </div>
              <p className="pricing-description">
                Perfect for casual traders getting started
              </p>
            </div>

            <div className="pricing-features">
              <h3>Includes:</h3>
              <ul>
                {freeFeatures.map((feature, index) => (
                  <li key={index}>
                    <span className="check-icon">&#10003;</span>
                    {feature}
                  </li>
                ))}
                <li className="not-included">
                  <span className="x-icon">&#10007;</span>
                  CSV export
                </li>
              </ul>
            </div>

            <div className="pricing-cta">
              {isAuthenticated ? (
                <Link to="/dashboard" className="btn btn-secondary btn-large">
                  Go to Dashboard
                </Link>
              ) : (
                <Link to="/register" className="btn btn-secondary btn-large">
                  Get Started Free
                </Link>
              )}
              <p className="pricing-note">No credit card required</p>
            </div>
          </div>

          <div className="pricing-card featured">
            <div className="pricing-badge">Recommended</div>
            <div className="pricing-header">
              <h2>Pro</h2>
              <div className="pricing-price">
                <span className="price-amount">$4.99</span>
                <span className="price-period">/month</span>
              </div>
              <p className="pricing-description">
                For serious traders who need unlimited access
              </p>
            </div>

            <div className="pricing-features">
              <h3>Everything in Free, plus:</h3>
              <ul>
                {proFeatures.map((feature, index) => (
                  <li key={index}>
                    <span className="check-icon">&#10003;</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <div className="pricing-cta">
              {isAuthenticated ? (
                <Link to="/profile?tab=subscription" className="btn btn-primary btn-large">
                  Upgrade to Pro
                </Link>
              ) : (
                <Link to="/register" className="btn btn-primary btn-large">
                  Get Started Free
                </Link>
              )}
              <p className="pricing-note">
                {isAuthenticated ? 'Upgrade anytime' : 'Start free, upgrade anytime'}
              </p>
            </div>
          </div>
        </section>

        {/* Feature Comparison */}
        <section className="pricing-compare">
          <h2>Feature Comparison</h2>
          <div className="compare-table-container">
            <table className="compare-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free</th>
                  <th>Pro</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Trading accounts</td>
                  <td>1</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Open options positions</td>
                  <td>10</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Closed trades history</td>
                  <td>Unlimited</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Stock positions</td>
                  <td>10</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Dashboard analytics</td>
                  <td><span className="check">&#10003;</span></td>
                  <td><span className="check">&#10003;</span></td>
                </tr>
                <tr>
                  <td>Advanced statistics</td>
                  <td><span className="check">&#10003;</span></td>
                  <td><span className="check">&#10003;</span></td>
                </tr>
                <tr>
                  <td>Performance metrics</td>
                  <td><span className="check">&#10003;</span></td>
                  <td><span className="check">&#10003;</span></td>
                </tr>
                <tr>
                  <td>CSV export</td>
                  <td><span className="x">&#10007;</span></td>
                  <td><span className="check">&#10003;</span></td>
                </tr>
                <tr>
                  <td>Time limit</td>
                  <td>Forever</td>
                  <td>Subscription-based</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="pricing-faq">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Is the Free plan really free forever?</h3>
              <p>
                Yes! The Free plan is completely free with no time limit.
                You can track up to 10 open options positions and 1 account forever.
              </p>
            </div>
            <div className="faq-item">
              <h3>What counts as an "open position"?</h3>
              <p>
                Open positions are CSPs, Covered Calls, and LEAPS that haven't been closed yet.
                Closed trades and stock positions don't count toward your limit.
              </p>
            </div>
            <div className="faq-item">
              <h3>What happens if I hit my limit?</h3>
              <p>
                You can still view all your data and close existing positions.
                To open new positions, either close some or upgrade to Pro.
              </p>
            </div>
            <div className="faq-item">
              <h3>Can I cancel Pro anytime?</h3>
              <p>
                Yes, you can cancel anytime. You'll keep Pro access until the end of
                your billing period, then switch back to Free.
              </p>
            </div>
            <div className="faq-item">
              <h3>What payment methods do you accept?</h3>
              <p>
                We accept all major credit cards through Stripe, a secure payment processor.
              </p>
            </div>
            <div className="faq-item">
              <h3>Do you offer refunds?</h3>
              <p>
                Since we offer a generous Free plan, you can try everything before upgrading.
                For details, see our <Link to="/refund-policy">Refund Policy</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="pricing-cta-section">
          <div className="cta-content">
            <h2>Ready to get started?</h2>
            <p>Join thousands of traders tracking their options strategies</p>
            {!isAuthenticated && (
              <div className="cta-buttons">
                <Link to="/register" className="btn btn-primary btn-large">
                  Get Started Free
                </Link>
                <Link to="/login" className="btn btn-secondary btn-large">
                  Log In
                </Link>
              </div>
            )}
            <p className="cta-links">
              <Link to="/how-to-use">See how it works</Link> • <Link to="/blog">Read the blog</Link>
            </p>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

export default Pricing;
